<script>
  // the <Footer> component:
  // - a "wizard" with 3 "steps", followed by 2 information panels with different actions
  // - allows the user to choose future model scenario attributes and update the map display

  import { onMount, onDestroy } from 'svelte';
  import {
    stormStore,
    returnPeriodStore,
    slrYearStore,

    photos360UrlsStore,
    criticalFacilitiesStatsStore,

    sideBySideComparisonStore,
    secondarySlrYearStore
  } from './stores.js';
  import appConfig from '../appConfig.js';

  let footerVisible = true;

  let footerClass = "footer-visible";

  $:if (footerVisible == true) {
      footerClass = "footer-visible";
    } else {
      footerClass = "footer-hidden";
    }

  onMount(() => {
    const defaultStormName = appConfig.defaultScenario.stormType;

    // set dependent store app default initial value for RP
    const defaultRPValue = appConfig.stormTypes[defaultStormName]
      .returnPeriodOptions.find(rpo => (
        rpo.returnPeriodNumber === appConfig.defaultScenario.returnPeriodNumber
      ));
    handleReturnPeriodClick(defaultRPValue);

    // reset the storm surge primary vs. alternate setting to 'primary'
    primaryOrAlternateStormSurge = 'primaryStormSurge';

    // set dependent store app default initial value for SLR year
    const defaultSlrYearOption = defaultRPValue.stormSurgeOptions[primaryOrAlternateStormSurge].slrYearOptions.find(yo => (
      yo.yearNumber === appConfig.defaultScenario.yearNumber
    ));
    handleSlrYearClick(defaultSlrYearOption);

    // finally, set the "entry-point" storm store
    stormStore.set(defaultStormName);
  });

  // for now use the verbose syntax Svelte store subscription and onDestroy tear-down
  // the concise syntax is very brief but can be less readable
  let currentStormSelection;
  const unsubscribeStormStore = stormStore.subscribe((newStormType) => {
    currentStormSelection = newStormType;
  });

  // on a button click, set the new year store value
  // which will also toggle the "active" CSS class
  function handleStormClick(stormName) {
    // clear return period and SLR+SS "dependent" stores since user must make their own choices
    // from left-to-right in the footer after choosing the storm type
    handleReturnPeriodClick(null);

    handleSlrYearClick(null);

    // finally, set the "entry-point" storm store
    stormStore.set(stormName);
  }

  // update the available return period options in the footer UI when the currentStormSelection changes
  $: returnPeriodOptions = currentStormSelection ? appConfig.stormTypes[currentStormSelection].returnPeriodOptions : [];
  // if there is only one choice available in panel 2, automatically choose it to let the user focus more quickly on their choices for panel 3
  $: if (returnPeriodOptions.length === 1) {
    handleReturnPeriodClick(returnPeriodOptions[0]);
  }

  let currentReturnPeriodSelection;
  const unsubscribeReturnPeriodStore = returnPeriodStore.subscribe(value => {
    currentReturnPeriodSelection = value;
  });

  function handleReturnPeriodClick(returnPeriod) {
    // set the new return period value
    returnPeriodStore.set(returnPeriod);
    // reset the storm surge primary vs. alternate setting to 'primary'
    primaryOrAlternateStormSurge = 'primaryStormSurge';
    // remove the current year choice
    slrYearStore.set(null);
    // turn off comparison mode
    sideBySideComparisonStore.set(false);
    secondarySlrYearStore.set(null);
  }

  // update the available storm surge options in the footer UI when the currentReturnPeriodSelection changes
  $: stormSurgeOptions = currentReturnPeriodSelection ? currentReturnPeriodSelection.stormSurgeOptions : null;

  let primaryOrAlternateStormSurge = 'primaryStormSurge';
  function handleStormSurgeToggle(e) {
    if (!e.target.checked) {
      primaryOrAlternateStormSurge = 'primaryStormSurge';
    } else {
      primaryOrAlternateStormSurge = 'alternateStormSurge';
    }

    // when changing between the primary or secondary storm surge scenario,
    // 1. update the SLR year selection
    if (currentSlrYearSelection) {
      const currentYearIndex = stormSurgeOptions[primaryOrAlternateStormSurge].slrYearOptions.findIndex(yo => (
        yo.yearNumber === currentSlrYearSelection.yearNumber
      ));
      const newYearOptionForNewStormSurge = stormSurgeOptions[primaryOrAlternateStormSurge].slrYearOptions[currentYearIndex];
      handleSlrYearClick(newYearOptionForNewStormSurge);
    }

    // 2. and run through the comparison toggle logic in case both the left and right map views need to be updated
    if ($sideBySideComparisonStore) {
      handleComparisonToggle({ target: { checked: $sideBySideComparisonStore } });
    }
  }

  let currentSlrYearSelection;
  const unsubscribeSlrYearStore = slrYearStore.subscribe(value => {
    currentSlrYearSelection = value;
  });

  function handleSlrYearClick(slrYear) {
    let slrYearAndStormSurgeInfo = getSlrYearAndStormSurgeInfo(slrYear);
    slrYearStore.set(slrYearAndStormSurgeInfo);
  }

  function handleComparisonToggle(e) {
    if (!e.target.checked) {
      // user switched off the comparison
      // set comparison Boolean to false and remove secondary SLR+SS info
      secondarySlrYearStore.set(null);
      sideBySideComparisonStore.set(false);
      return;
    }

    if (!currentReturnPeriodSelection && !currentSlrYearSelection) {
      // comparison not allowed if there aren't already primary return period and SLR+SS infos available
      // set comparison Boolean to false and remove secondary SLR+SS info
      secondarySlrYearStore.set(null);
      sideBySideComparisonStore.set(false);
      return;
    }

    // user switched on the comparison and required primary return period and SLR+SS infos are available
    // set left (earlier simulation) / right (later simulation) return period and SLR+SS infos
    // set comparison Boolean to true which will let App.svelte render 2 MapViews
    slrYearStore.set(
      getSlrYearAndStormSurgeInfo(currentReturnPeriodSelection.stormSurgeOptions[primaryOrAlternateStormSurge].slrYearOptions[0])
    );
    secondarySlrYearStore.set(
      getSlrYearAndStormSurgeInfo(currentReturnPeriodSelection.stormSurgeOptions[primaryOrAlternateStormSurge].slrYearOptions[1])
    );
    sideBySideComparisonStore.set(true);
  }

  // important: run this function to get the correct SLR and storm surge info when setting new app state in these svelte stores:
  // (but not necessary when setting to `null`)
  // - slrYearStore
  // - secondarySlrYearStore
  function getSlrYearAndStormSurgeInfo(slrYear) {
    // Mixin the `slrYear` info with the its parent config object info about `stormSurgeNumber`.
    // This helps the EsriMapView when filtering webmap layers and displaying this info on the mapview(s).
    // Why? This supports an app data structure overhaul in August 2020 that allowed more control over configuring and toggling between storm surge scenarios.
    if (slrYear && currentReturnPeriodSelection.stormSurgeOptions) {
      return Object.assign({
        stormSurgeNumber: currentReturnPeriodSelection.stormSurgeOptions[primaryOrAlternateStormSurge].stormSurgeNumber
      }, slrYear);
    } else {
      return slrYear;
    }
  }

  onDestroy(() => {
    unsubscribeStormStore();
    unsubscribeReturnPeriodStore();
    unsubscribeSlrYearStore();
  });
</script>

<button title="Show/Hide tooblar" class="show-hide-button row" on:click={() => footerVisible = !footerVisible}>
  {#if footerVisible}
  <calcite-icon icon="chevronDown"></calcite-icon>
  {/if}
  {#if !footerVisible}
  <calcite-icon icon="chevronUp"></calcite-icon>
  {/if}
</button>
<footer class="row {footerClass}">
  {#if footerVisible===true}
  <!-- panel 1 -->
  <div class="col space-between" style="min-width: 100px;">
    <div class="row panel-title">
      <svg width="32" height="32" viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M20 28H19V11.238L14.686 12.964L14.314 12.036L20 9.76201V28ZM38 19C38 15.2422 36.8857 11.5687 34.7979 8.44417C32.7102 5.31964 29.7428 2.88436 26.271 1.4463C22.7992 0.00823236 18.9789 -0.368031 15.2933 0.365088C11.6077 1.09821 8.22218 2.90778 5.56498 5.56498C2.90778 8.22218 1.09821 11.6077 0.365088 15.2933C-0.368031 18.9789 0.00823236 22.7992 1.4463 26.271C2.88436 29.7428 5.31964 32.7102 8.44417 34.7979C11.5687 36.8857 15.2422 38 19 38C24.0373 37.9942 28.8667 35.9905 32.4286 32.4286C35.9905 28.8667 37.9942 24.0373 38 19ZM37 19C37 22.5601 35.9443 26.0402 33.9665 29.0003C31.9886 31.9604 29.1774 34.2675 25.8883 35.6298C22.5992 36.9922 18.98 37.3487 15.4884 36.6541C11.9967 35.9596 8.78943 34.2453 6.27209 31.7279C3.75474 29.2106 2.04041 26.0033 1.34587 22.5116C0.651339 19.02 1.0078 15.4008 2.37018 12.1117C3.73255 8.82264 6.03966 6.01142 8.99974 4.03356C11.9598 2.05569 15.4399 1.00001 19 1.00001C23.7723 1.0053 28.3476 2.90343 31.7221 6.27793C35.0966 9.65244 36.9947 14.2277 37 19Z" fill="#E5E5E5" />
      </svg>

      <span class="title-text">Extreme Weather Event</span>
    </div>
    <div class="outer center">
      <div class="row buttons-row storm">
        <button
          class="storm"
          on:click="{() => handleStormClick('thunderstorm')}"
          class:active="{currentStormSelection === 'thunderstorm'}"
        >
          {@html appConfig.stormTypes.thunderstorm.buttonIcon}
  
          {appConfig.stormTypes.thunderstorm.title}
        </button>
  
        <button
          class="storm storm-2-4"
          on:click="{() => handleStormClick('noreaster')}"
          class:active="{currentStormSelection === 'noreaster'}"
        >
          {@html appConfig.stormTypes.noreaster.buttonIcon}
  
          {appConfig.stormTypes.noreaster.title}
        </button>
      </div>
  
      <div class="row buttons-row storm">
        <button
          class="storm"
          on:click="{() => handleStormClick('tropical')}"
          class:active="{currentStormSelection === 'tropical'}"
        >
          {@html appConfig.stormTypes.tropical.buttonIcon}
  
          {appConfig.stormTypes.tropical.title}
        </button>
  
        <button
          class="storm storm-2-4"
          on:click="{() => handleStormClick('frontal')}"
          class:active="{currentStormSelection === 'frontal'}"
        >
          {@html appConfig.stormTypes.frontal.buttonIcon}
  
          {appConfig.stormTypes.frontal.title}
        </button>
      </div>
    </div>

    
  </div>

  <!-- panel 2 -->
  <div class="col space-between" style="min-width: 182px;">
    <div class="row panel-title">
      <svg width="32" height="32" viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M19 38C15.2422 38 11.5687 36.8857 8.44417 34.7979C5.31964 32.7102 2.88436 29.7428 1.4463 26.271C0.00823236 22.7992 -0.368031 18.9789 0.365088 15.2933C1.09821 11.6077 2.90778 8.22218 5.56498 5.56498C8.22218 2.90778 11.6077 1.09821 15.2933 0.365088C18.9789 -0.368031 22.7992 0.00823236 26.271 1.4463C29.7428 2.88436 32.7102 5.31964 34.7979 8.44417C36.8857 11.5687 38 15.2422 38 19C37.9942 24.0373 35.9905 28.8667 32.4286 32.4286C28.8667 35.9905 24.0373 37.9942 19 38ZM19 1.00001C15.4399 1.00001 11.9598 2.05569 8.99974 4.03356C6.03966 6.01142 3.73255 8.82264 2.37018 12.1117C1.0078 15.4008 0.651339 19.02 1.34587 22.5116C2.04041 26.0033 3.75474 29.2106 6.27209 31.7279C8.78943 34.2453 11.9967 35.9596 15.4884 36.6541C18.98 37.3487 22.5992 36.9922 25.8883 35.6298C29.1774 34.2675 31.9886 31.9604 33.9665 29.0003C35.9443 26.0402 37 22.5601 37 19C36.9947 14.2277 35.0966 9.65244 31.7221 6.27793C28.3476 2.90343 23.7723 1.0053 19 1.00001ZM25 27H15.002C15.031 24.92 15.43 23.88 21.012 20.32C24.318 18.213 24.837 15.225 24.278 13.307C24 12.3238 23.3991 11.4628 22.572 10.8629C21.7449 10.263 20.7399 9.959 19.719 10C14.168 10 14.104 16.038 14.104 16.1H15.104C15.104 15.891 15.156 11 19.719 11C20.5223 10.9644 21.3141 11.2003 21.967 11.6696C22.6199 12.1389 23.0958 12.8143 23.318 13.587C23.78 15.173 23.313 17.667 20.474 19.478C14.597 23.226 14 24.344 14 27.5V28H25V27Z" fill="#E5E5E5" />
      </svg>

      <span class="title-text">Amount of Rainfall</span>
    </div>

    <div class="outer center">
      <div class="inner center">
        <div class="row buttons-row" style="align-items: flex-start; justify-content: center; height: 100%; max-height: 250px">
          {#each returnPeriodOptions as returnPeriodOption, index}
            <div class="return-period-container" style="display: flex; flex-direction: column; height: 150px; justify-content: space-evenly; align-items: flex-start;">
              <div class="return-period-text">
                {returnPeriodOption.returnPeriodText}
              </div>
              <button
                on:click="{() => handleReturnPeriodClick(returnPeriodOption)}"
                class:active="{(currentReturnPeriodSelection && currentReturnPeriodSelection.rainfallText) === returnPeriodOption.rainfallText}"
                class="return-period"
              >
                <span style="height: {(index + 3) * 10}px;">
                  {@html appConfig.stormTypes[currentStormSelection].buttonIcon}
                </span>
              </button>
              <div class="return-period-text">
                {returnPeriodOption.rainfallText}
              </div>
            </div>
          {/each}
        </div>
      </div>
    </div>

    

    {#if currentStormSelection}
      <div class="storm-surge-duration">{appConfig.stormTypes[currentStormSelection].stormDurationText}</div>
    {/if}
  </div>

  <!-- panel 3 -->
  <div class="col space-between" style="min-width: 130px;">
    <div class="row panel-title" style="margin-bottom: 10px">
      <svg width="32" height="32" viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M19.095 28C17.7568 28.0109 16.4689 27.4907 15.5138 26.5534C14.5587 25.616 14.0143 24.3382 14 23H15C15.0266 24.0689 15.4695 25.0852 16.2344 25.8324C16.9993 26.5796 18.0257 26.9985 19.095 27C19.6157 26.9993 20.131 26.8947 20.6107 26.6923C21.0904 26.4898 21.5248 26.1936 21.8885 25.8211C22.2523 25.4485 22.5379 25.0071 22.7288 24.5226C22.9196 24.0382 23.0119 23.5205 23 23C22.9987 21.9395 22.5768 20.9229 21.827 20.173C21.0771 19.4232 20.0605 19.0013 19 19V18C20.991 18 23 16.995 23 14.75C22.9356 13.7329 22.4862 12.7785 21.7431 12.0809C21.0001 11.3834 20.0192 10.9951 19 10.9951C17.9808 10.9951 16.9999 11.3834 16.2569 12.0809C15.5138 12.7785 15.0644 13.7329 15 14.75H14C14.0678 13.4697 14.6241 12.2641 15.5544 11.3818C16.4846 10.4995 17.7179 10.0077 19 10.0077C20.2821 10.0077 21.5154 10.4995 22.4456 11.3818C23.3759 12.2641 23.9322 13.4697 24 14.75C24.0058 15.586 23.7519 16.4031 23.2732 17.0885C22.7946 17.7738 22.1149 18.2936 21.328 18.576C22.1337 19.0005 22.8084 19.6369 23.2792 20.4164C23.7501 21.196 23.9993 22.0893 24 23C24.0133 23.6523 23.8962 24.3006 23.6557 24.907C23.4151 25.5135 23.0558 26.0657 22.5989 26.5314C22.1421 26.9972 21.5968 27.367 20.9951 27.6191C20.3934 27.8713 19.7474 28.0008 19.095 28ZM38 19C38 15.2422 36.8857 11.5687 34.7979 8.44417C32.7102 5.31964 29.7428 2.88436 26.271 1.4463C22.7992 0.00823236 18.9789 -0.368031 15.2933 0.365088C11.6077 1.09821 8.22218 2.90778 5.56498 5.56498C2.90778 8.22218 1.09821 11.6077 0.365088 15.2933C-0.368031 18.9789 0.00823236 22.7992 1.4463 26.271C2.88436 29.7428 5.31964 32.7102 8.44417 34.7979C11.5687 36.8857 15.2422 38 19 38C24.0373 37.9942 28.8667 35.9905 32.4286 32.4286C35.9905 28.8667 37.9942 24.0373 38 19ZM37 19C37 22.5601 35.9443 26.0402 33.9665 29.0003C31.9886 31.9604 29.1774 34.2675 25.8883 35.6298C22.5992 36.9922 18.98 37.3487 15.4884 36.6541C11.9967 35.9596 8.78943 34.2453 6.27209 31.7279C3.75474 29.2106 2.04041 26.0033 1.34587 22.5116C0.651339 19.02 1.0078 15.4008 2.37018 12.1117C3.73255 8.82264 6.03966 6.01142 8.99974 4.03356C11.9598 2.05569 15.4399 1.00001 19 1.00001C23.7723 1.0053 28.3476 2.90343 31.7221 6.27793C35.0966 9.65244 36.9947 14.2277 37 19Z" fill="#E5E5E5" />
      </svg>

      <span class="title-text long-title">Sea Level Rise (SLR) and Storm Surge from
        <abbr
          title="Baseline conditions represent a typical high tide in the Boston area. This baseline high tide is based on an average value in the tide data range from 1999-2017."
          style="text-decoration: underline;"
        >
          Baseline Condition
        </abbr>
      </span>
    </div>

    {#if stormSurgeOptions}
      <div class="row buttons-row" style="align-items: flex-end; justify-content: center; height: 100%; min-width: 150px;">
        {#each stormSurgeOptions[primaryOrAlternateStormSurge].slrYearOptions as slrYearOption}
          <div class="storm-surge-opts" style="display: flex; flex-direction: column; height: 80%; width: 40%; text-align: center; justify-content: space-evenly; align-items: center; margin-top: 10x;">
            <button
              on:click="{() => {
                // prevent this from running if comparison mode is turned on
                if ($sideBySideComparisonStore) {
                  return;
                }
                handleSlrYearClick(slrYearOption);
              }}"
              class:active="{((currentSlrYearSelection && currentSlrYearSelection.slrText) === slrYearOption.slrText) || $sideBySideComparisonStore}"
              class="slr-ss"
            >
              {slrYearOption.buttonText}
            </button>
            <div class="slr-year-opt" style="margin-top: 8px">
              {slrYearOption.slrText}
            </div>
            {#if stormSurgeOptions[primaryOrAlternateStormSurge].stormSurgeNumber}
              <div class="storm-surge-number" style="color: #fff; font-style: italic; margin: 8px">
                + Storm Surge
              </div>
            {/if}
          </div>
        {/each}
      </div>

      <!-- only show the storm surge comparison toggle when there are both primary and aternate storm surge options to choose from -->
      {#if stormSurgeOptions.alternateStormSurge}
        <label style="cursor: pointer; text-align: center; margin-bottom: 15px;">
          <div style="margin-bottom: 2px;">Change Storm Surge</div>
          <span style="margin-right: 8px;">
            {#if stormSurgeOptions.primaryStormSurge.stormSurgeNumber}
              {stormSurgeOptions.primaryStormSurge.stormSurgeNumber} Year
            {:else}
              None
            {/if}
          </span>
          <calcite-switch
            checked="{primaryOrAlternateStormSurge && primaryOrAlternateStormSurge === 'alternateStormSurge'}"
            scale="s"
            theme="dark"
            on:calciteSwitchChange="{(e) => handleStormSurgeToggle(e)}"
          >
          </calcite-switch>
          <span style="margin-left: 8px;">
            {#if stormSurgeOptions.alternateStormSurge.stormSurgeNumber}
              {stormSurgeOptions.alternateStormSurge.stormSurgeNumber} Year
            {:else}
              None
            {/if}
          </span>
        </label>

        <!-- TODO: alternate UI/UX from switch above -->
        <!-- <div>Change Storm Surge</div>
        <div style="display: flex; flex-direction: row; justify-content: center; width: 100%; margin-bottom: 15px;">
          <button style="border-right: 0;">{stormSurgeOptions.primaryStormSurge.stormSurgeNumber} Year</button>
          <button>{stormSurgeOptions.alternateStormSurge.stormSurgeNumber} Year</button>
        </div> -->

      <!--
        otherwise just show the primary storm surge text info
        instead of the ability to change between primary/alternate storm surge scenarios
      -->
      {:else if stormSurgeOptions && stormSurgeOptions.primaryStormSurge.stormSurgeNumber}
        <div class="storm-surge-duration" style="margin-bottom: 20px; margin-top: 20px;">{stormSurgeOptions.primaryStormSurge.stormSurgeNumber} Year Storm Surge</div>
      {:else}
        <div class="storm-surge-duration" style="margin-bottom: 20px;">No Storm Surge</div>
      {/if}

      <!-- only show the SLR years side-by-side comparison toggle when there are multiple SLR year options to choose from -->
      {#if stormSurgeOptions[primaryOrAlternateStormSurge].slrYearOptions.length}
        <label style="cursor: pointer;">
          <calcite-switch
            checked="{$sideBySideComparisonStore}"
            scale="s"
            theme="dark"
            on:calciteSwitchChange="{(e) => handleComparisonToggle(e)}"
          >
          </calcite-switch>
          <span class="storm-surge-duration" style="margin-left: 8px;">
            Compare 2030 &amp; 2070
          </span>
        </label>
      {/if}

    {/if} <!-- end {#if stormSurgeOptions} -->
  </div>

  <!-- panel 4 -->
  <div class="col space-between">
    <div class="row panel-title">
      <svg width="32" height="32" viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M21 28H20V23H12.077L21 9.31801V22H24V23H21V28ZM13.923 22H20V12.682L13.923 22ZM19 38C15.2422 38 11.5687 36.8857 8.44417 34.7979C5.31964 32.7102 2.88436 29.7428 1.4463 26.271C0.00823236 22.7992 -0.368031 18.9789 0.365088 15.2933C1.09821 11.6077 2.90778 8.22218 5.56498 5.56498C8.22218 2.90778 11.6077 1.09821 15.2933 0.365088C18.9789 -0.368031 22.7992 0.00823236 26.271 1.4463C29.7428 2.88436 32.7102 5.31964 34.7979 8.44417C36.8857 11.5687 38 15.2422 38 19C37.9942 24.0373 35.9905 28.8667 32.4286 32.4286C28.8667 35.9905 24.0373 37.9942 19 38ZM19 1.00001C15.4399 1.00001 11.9598 2.05569 8.99974 4.03356C6.03966 6.01142 3.73255 8.82264 2.37018 12.1117C1.0078 15.4008 0.651339 19.02 1.34587 22.5116C2.04041 26.0033 3.75474 29.2106 6.27209 31.7279C8.78943 34.2453 11.9967 35.9596 15.4884 36.6541C18.98 37.3487 22.5992 36.9922 25.8883 35.6298C29.1774 34.2675 31.9886 31.9604 33.9665 29.0003C35.9443 26.0402 37 22.5601 37 19C36.9947 14.2277 35.0966 9.65244 31.7221 6.27793C28.3476 2.90343 23.7723 1.0053 19 1.00001Z" fill="#E5E5E5" />
      </svg>

      {#if $photos360UrlsStore.length}
        <span class="title-text">
          <abbr
            title="360 degree tours are provided at well-known locations throughout the City where 5 feet or less of flooding was predicted by the model. These tours are fully interactive and represent model predicted peak flood depths."
            style="text-decoration: underline;"
          >
            360 Tour
          </abbr>
          <br>
          Click
          <img alt="360 tour map symbol" width="18.666666666666664" height="18.666666666666664" style="vertical-align: bottom;" src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABMAAAATCAYAAAByUDbMAAAACXBIWXMAAA7EAAAOxAGVKw4bAAACcUlEQVQ4ja2UT0tqQRyGH6jQ47/o6pWjiBIuykIXUi5z0fIiuJKWtQhc9Un6DhdbtnDRH4goETdyshZFROYitfRgB9Ggo8Hhwl3MCUSk4nIHhlnMbx7e953fzDT/cUx/o2bGXI1/hdmAeSAMBAEH8Aa0gFugPgk+CeYB1iWJlCyz5PMhSxJOXeddVWlpGleDAadACVA/g3msVn55vWzHYqwkk1giEbDbQddx1Gp4CgUWFYV4r4dsGOyPAkdhNmDd62U7nSaeyWAJBODpCV5ewO2GVAoSCSy5HNGjIzbbbbpAHhiMw+YliVQsxkomg8XjgXweymXodsFmg3gcMhnIZpnq94kcHpIeDrkHLsdhYVlmKZkUivJ5OD+HaBSSSahWoVgEhwO2tiCZxHJxwXK9TngcNgMEfT7kSERYK5cFaGcHfD5oNmF3FyoVAQ+FwO9ntl7np3neGFXmkCScdrvIqNsVh3w+sRkMwsICPDxApwM/foDLhROYG4cZwJuu867rONxukVG1KhQFg6Cq0GqB1QqSBLoOuo5hhm+MZ9ZSVVq1Gp5USoRdLAprCwsCdHMDq6sQCMDJCTQatIHmJNitpnFVKLCYSGDJZETYlYqwZrUK0MYGvL5CqYShaVwDd5P6rD4YcKooxPf2iGazTJm3RqcjrAUCApTL8UdRuBsOOQEeJ8EMoNTrIR8fs9nvE1lbwxIKibB1XVgrlTAUhTtN4zdw9tGw4zAA1TDYf36me3BAWlFY9vuZdblw6jpGo0Fb07g2FZ3xxdvELMgPh9zX64TNPpozFTTNjB5HFX0Gwyy8NOeMOQ2++NO+8zl+CfkYfwHHi/a/cVpwuwAAAABJRU5ErkJggg==">
          on Map
        </span>
      {/if}
    </div>

    <div class="outer center">
      <div class="inner center">
        <div style="height: {$photos360UrlsStore.length ? 100 : 60}%; display: flex !important; align-items: center; justify-content: center">
          {#if $photos360UrlsStore.length}
            <!-- `allowfullscreen` is considered deprecated, but it helps make fullscreen work in FF -->
            <iframe
              src={$photos360UrlsStore[0]}
              allow="fullscreen"
              allowfullscreen
              title="view a 360 degree photo tour"
              style="border: none; width: 100%;"
            ></iframe>
          {:else}
            <span class="title-text" style="margin: 0px !important;font-size: 1.3rem; text-align: center">
              <abbr
                title="360 degree tours are provided at well-known locations throughout the City where 5 feet or less of flooding was predicted by the model. These tours are fully interactive and represent model predicted peak flood depths."
                style="text-decoration: underline;"
              >
                360 Tour
              </abbr>
              <br>
              Click
              <img alt="360 tour map symbol" width="18.666666666666664" height="18.666666666666664" style="vertical-align: bottom; margin-bottom: 3px;" src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABMAAAATCAYAAAByUDbMAAAACXBIWXMAAA7EAAAOxAGVKw4bAAACcUlEQVQ4ja2UT0tqQRyGH6jQ47/o6pWjiBIuykIXUi5z0fIiuJKWtQhc9Un6DhdbtnDRH4goETdyshZFROYitfRgB9Ggo8Hhwl3MCUSk4nIHhlnMbx7e953fzDT/cUx/o2bGXI1/hdmAeSAMBAEH8Aa0gFugPgk+CeYB1iWJlCyz5PMhSxJOXeddVWlpGleDAadACVA/g3msVn55vWzHYqwkk1giEbDbQddx1Gp4CgUWFYV4r4dsGOyPAkdhNmDd62U7nSaeyWAJBODpCV5ewO2GVAoSCSy5HNGjIzbbbbpAHhiMw+YliVQsxkomg8XjgXweymXodsFmg3gcMhnIZpnq94kcHpIeDrkHLsdhYVlmKZkUivJ5OD+HaBSSSahWoVgEhwO2tiCZxHJxwXK9TngcNgMEfT7kSERYK5cFaGcHfD5oNmF3FyoVAQ+FwO9ntl7np3neGFXmkCScdrvIqNsVh3w+sRkMwsICPDxApwM/foDLhROYG4cZwJuu867rONxukVG1KhQFg6Cq0GqB1QqSBLoOuo5hhm+MZ9ZSVVq1Gp5USoRdLAprCwsCdHMDq6sQCMDJCTQatIHmJNitpnFVKLCYSGDJZETYlYqwZrUK0MYGvL5CqYShaVwDd5P6rD4YcKooxPf2iGazTJm3RqcjrAUCApTL8UdRuBsOOQEeJ8EMoNTrIR8fs9nvE1lbwxIKibB1XVgrlTAUhTtN4zdw9tGw4zAA1TDYf36me3BAWlFY9vuZdblw6jpGo0Fb07g2FZ3xxdvELMgPh9zX64TNPpozFTTNjB5HFX0Gwyy8NOeMOQ2++NO+8zl+CfkYfwHHi/a/cVpwuwAAAABJRU5ErkJggg==">
              on Map
            </span>
          {/if}
        </div>
      </div>
    </div>
  </div>

  <!-- panel 5 -->
  <div class="col space-between" style="min-width: 100px">
    <div class="row panel-title">
      <svg width="32" height="32" viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M18.5 28C17.0591 28.0155 15.6708 27.4588 14.6398 26.4521C13.6087 25.4454 13.019 24.0709 13 22.63H14C14.0349 23.8003 14.5243 24.9109 15.3644 25.7263C16.2045 26.5417 17.3292 26.9978 18.5 26.9978C19.6708 26.9978 20.7955 26.5417 21.6356 25.7263C22.4757 24.9109 22.9651 23.8003 23 22.63C22.9863 21.4286 22.5118 20.2782 21.6744 19.4166C20.8369 18.555 19.7006 18.0479 18.5 18C17.2127 17.9399 15.9251 18.1141 14.7 18.514L14.002 18.815L14 10H23V11H15V17.374C16.1468 17.1057 17.3224 16.9801 18.5 17C19.9696 17.0368 21.3668 17.6457 22.3941 18.6973C23.4214 19.7489 23.9976 21.1599 24 22.63C23.981 24.0709 23.3913 25.4454 22.3603 26.4521C21.3292 27.4588 19.9409 28.0155 18.5 28ZM38 19C38 15.2422 36.8857 11.5687 34.7979 8.44417C32.7102 5.31964 29.7428 2.88436 26.271 1.4463C22.7992 0.00823236 18.9789 -0.368031 15.2933 0.365088C11.6077 1.09821 8.22218 2.90778 5.56498 5.56498C2.90778 8.22218 1.09821 11.6077 0.365088 15.2933C-0.368031 18.9789 0.00823236 22.7992 1.4463 26.271C2.88436 29.7428 5.31964 32.7102 8.44417 34.7979C11.5687 36.8857 15.2422 38 19 38C24.0373 37.9942 28.8667 35.9905 32.4286 32.4286C35.9905 28.8667 37.9942 24.0373 38 19ZM37 19C37 22.5601 35.9443 26.0402 33.9665 29.0003C31.9886 31.9604 29.1774 34.2675 25.8883 35.6298C22.5992 36.9922 18.98 37.3487 15.4884 36.6541C11.9967 35.9596 8.78943 34.2453 6.27209 31.7279C3.75474 29.2106 2.04041 26.0033 1.34587 22.5116C0.651339 19.02 1.0078 15.4008 2.37018 12.1117C3.73255 8.82264 6.03966 6.01142 8.99974 4.03356C11.9598 2.05569 15.4399 1.00001 19 1.00001C23.7723 1.0053 28.3476 2.90343 31.7221 6.27793C35.0966 9.65244 36.9947 14.2277 37 19Z" fill="#E5E5E5" />
      </svg>

      <span class="title-text">Impact Forecast</span>
    </div>
    <div class="outer center">
      <div class="inner center" style="height: 100%">
        <div class="row impact-forecast-container" style="border: 1px solid rgba(255, 255, 255, 0.7); border-radius: 5px; padding: 5px; justify-content: space-around; align-items: center; height: 100%; max-height: 180px; max-width: 250px;">
          {#each Object.keys($criticalFacilitiesStatsStore) as statsKey}
            <div style="text-align: center; margin: 0 4px; max-width: 70px;">
              <div class="impact-forecast-title" style="font-size: 0.8em; margin: 2px 0; height: 30px; display: flex; align-items: center;">
                {statsKey}
              </div>
              <div class="impact-forecast-numbers" style="font-size: 1.3em; color: #fff;">
                {#if $sideBySideComparisonStore}
                  --
                {:else}
                  {$criticalFacilitiesStatsStore[statsKey] ? $criticalFacilitiesStatsStore[statsKey] : "--"}
                {/if}
              </div>
            </div>
          {/each}
        </div>
      </div>
    </div>
  </div>
  {/if}
</footer>

<style>
  footer * {
    color: rgba(255, 255, 255, 0.7);
  }

  footer .col {
    border-right: 1px solid gray;
    padding: 8px 16px 16px 16px;
  }

  footer .col:last-of-type {
    border: 0;
  }

  .panel-title {
    align-items: center;
    width: 100%;
    height: 60px;
  }

  .panel-title svg {
    height: 60px;
  }

  .panel-title .title-text {
    width: 70%;
    margin-left: 20px;
  }

  .buttons-row {
    justify-content: space-evenly;
    width: 100%;
  }

  .buttons-row.storm {
    justify-content: center;
    margin: 4px 0;
  }

  button {
    /* browser reset */
    -webkit-appearance: none;
       -moz-appearance: none;
            appearance: none;

    border: 1px solid rgba(255, 255, 255, 0.7);
    background-color: transparent;
    color: rgba(255, 255, 255, 0.7);
    padding: 8px 8px;
    font-family: inherit;
    font-size: inherit;
    cursor: pointer;

    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  button.storm {
    width: 100px;
    height: 80px;
    margin: 0 0.25em;
  }

  button.return-period {
    width: 70px;
    height: 70px;
  }

  button.slr-ss {
    font-size: 1.3em;
    padding: 8px;
  }

  :global(button.storm svg) {
    height: 40px;
    /* attempt to block title properties from rendering as tooltips */
    pointer-events: none;
  }

  :global(button.return-period svg) {
    height: 100%;
    display: block;
    /* attempt to block title properties from rendering as tooltips */
    pointer-events: none;
  }

  :global(button path) {
    fill: rgba(255, 255, 255, 0.7) !important;
  }

  button:hover,
  /* also capture the child subtitle text div in the RP buttons */
  button:hover div {
    color: #404040;
    background-color: #f0f0f0;
  }

  :global(button:hover path) {
    fill: #404040 !important;;
    /* stroke: #404040; */
  }

  .active,
  /* also capture the child subtitle text div in the RP buttons */
  .active div {
    color: #404040;
    background-color: #f0f0f0;
  }

  :global(.active path) {
    fill: #404040 !important;
    /* stroke: #404040; */
    /* stroke-width: 2.5px; */
  }

  .space-between {
    justify-content: space-between; 
    height: 100%; 
  }
  
  .center {
    display: flex; 
    flex-direction: column; 
    justify-content: center !important; 
    align-items: center; 
  }

  .outer {
    height: 100%; 
  }

/* evenly space storm buttons when they are in a column*/
@media screen and (max-width: 1064px) {
  .storm-2-4 {
    margin-top: 0.5em !important;
  }
}
  /* footer styles for small desktop*/
@media screen and (min-width: 780px) and (max-width: 1250px) {
  .panel-title {
    margin-bottom: 0px !important;
  }

  /* override inline styles with !important */
  .title-text {
    width: 60% !important;
    margin-left: 10px !important;
    font-size: 14px !important;
  }

  button.storm {
    height: 64px !important;
    width: 80px !important;
  }

  .return-period-container {
    height: 125px !important;
    margin: 0px 4px;
  }

  .impact-forecast-numbers {
    font-size: 18px !important;
  }

  .storm-surge-opts {
    height: 80% !important;
  }

  button.slr-ss,
  .storm-surge-number,
  .slr-year-opt,
  .compare-slider,
  .storm-surge-duration,
  .return-period-text {
    font-size: 13px !important;
  }

  .impact-forecast-container {
    max-height: none !important;
  }
}

/* footer styles for ipad mini and smaller*/
@media screen and (min-width: 731px) and (max-width: 779px) {
  .panel-title {
    margin-bottom: 0px;
    width: 110% !important;
  }

    /* override inline styles with !important */
    .title-text {
    width: 60% !important;
    margin-left: 10px !important;
    font-size: 14px !important;
  }

  button.storm {
    height: 64px !important;
    width: 80px !important;
  }

  .return-period-container {
    height: 125px !important;
    margin: 0px 4px;
  }

  .storm-surge-opts {
    height: 75% !important;
  }

  button.slr-ss,
  .storm-surge-number,
  .slr-year-opt,
  .compare-slider,
  .storm-surge-duration,
  .return-period-text {
    font-size: 13px !important;
  }
  /* panel-5  */
  .impact-forecast-container {
    max-height: none !important;
  }
  .impact-forecast-numbers {
    font-size: 16px !important;
  }
}

/* footer styles for smaller than ipad mini*/
@media screen and (min-width: 681px) and (max-width: 730px){
  button.storm {
    height: 64px !important;
    width: 80px !important;
  }

  .return-period-container {
    height: 125px !important;
    margin: 0px 4px;
  }

  .storm-surge-opts {
    height: 75% !important;
  }

  button.slr-ss,
  .storm-surge-number,
  .slr-year-opt,
  .compare-slider,
  .storm-surge-duration,
  .return-period-text {
    font-size: 12px !important;
  }

  /* panel-5  */
  .impact-forecast-container {
    max-height: none !important;
  }
  .impact-forecast-numbers {
    font-size: 16px !important;
  }
}

/* min footer size*/
@media screen and (max-width: 680px) {
  .panel-title {
    width: 100% !important;
  }
  button.storm {
    height: 64px !important;
    width: 80px !important;
  }

  .return-period-container {
    height: 125px !important;
    margin: 0px 4px;
  }
  .storm-surge-opts {
    height: 75% !important;
  }
}

.footer-hidden {
  visibility: hidden;
}
</style>
